/**
 * keyboardInset — the on-screen keyboard, expressed as an ordinary height
 * change (2026-09-14).
 *
 * ANDROID IS ALREADY FINE, and that is worth stating before the fix. The app
 * ships `interactive-widget=resizes-content` in its viewport meta, so Chrome
 * on Android shrinks the LAYOUT viewport when the keyboard opens: `100dvh`
 * moves, the transcript's `clientHeight` moves, the scroll controller's
 * ResizeObserver fires, and a pinned viewport re-sticks. Nothing here is
 * needed there.
 *
 * iOS SAFARI DOES NOT IMPLEMENT IT. There the keyboard changes only the
 * VISUAL viewport; the layout viewport, `dvh` and `clientHeight` are all
 * unmoved. Two things follow, and the second is the one that matters:
 *
 *   1. The composer sits behind the keyboard. The fullscreen pane is
 *      `position: fixed; bottom: 0` and the page is scroll-locked, which is
 *      the combination iOS's "scroll the focused input into view" heuristic
 *      handles worst.
 *   2. "Pinned" quietly stops meaning "at the bottom of what I can see".
 *      `clientHeight` never changed, so the ResizeObserver never fired,
 *      `scrollTop === scrollHeight` still holds — and the last message is
 *      under the keyboard. The requirement that a new message brings the
 *      thread down is then satisfied arithmetically and violated visually.
 *
 * THE FIX IS TO MAKE THE KEYBOARD A REAL HEIGHT CHANGE. Publish the inset as
 * a custom property, let the fullscreen pane subtract it from its own bottom,
 * and every existing mechanism keeps working unmodified — the controller's
 * ResizeObserver re-sticks a pinned viewport, and the reflow machinery
 * correctly ignores it because only the height moved.
 *
 * It self-disables where it is not needed: on Android the layout viewport has
 * already shrunk, so `clientHeight` and the visual viewport agree and the
 * inset computes to ~0. No user-agent sniffing, no flag.
 *
 * Deliberately NOT inside useTranscriptScroll: four surfaces share that
 * controller, and the whole point of this shape is that the controller never
 * has to know a keyboard exists.
 *
 * Leaf module: the arithmetic is pure and has no DOM access, so it is
 * testable without a browser. What it CANNOT test is whether a real iOS
 * keyboard reports these numbers — headless Chromium has no virtual keyboard,
 * and synthesising a visualViewport resize would be testing the mock.
 */

/** The custom property the fullscreen pane subtracts from its own bottom. */
export const KEYBOARD_INSET_VAR = '--prest0n-keyboard-inset';

/**
 * Movement below this is ignored.
 *
 * Writing the property changes layout, which can produce another viewport
 * event; without a dead band that is a feedback loop. Two pixels is below
 * anything a keyboard does and above the sub-pixel noise of a rounded
 * viewport height.
 */
export const KEYBOARD_INSET_DEAD_BAND_PX = 2;

export interface KeyboardInsetMeasurement {
  /** `document.documentElement.clientHeight` — the LAYOUT viewport. */
  layoutViewportHeight: number;
  /** `visualViewport.height`. */
  visualViewportHeight: number;
  /** `visualViewport.offsetTop` — non-zero while the visual viewport is panned. */
  visualViewportOffsetTop: number;
}

/**
 * How much of the layout viewport the keyboard is covering, in px.
 *
 * Zero on Android (the layout viewport already shrank, so the two agree) and
 * zero on every desktop browser. Clamped at zero: a visual viewport TALLER
 * than the layout viewport happens transiently during pinch-zoom and is not
 * a keyboard.
 */
export function keyboardInsetPx(m: KeyboardInsetMeasurement): number {
  const { layoutViewportHeight, visualViewportHeight, visualViewportOffsetTop } = m;
  if (!Number.isFinite(layoutViewportHeight) || !Number.isFinite(visualViewportHeight)) return 0;
  if (!(layoutViewportHeight > 0) || !(visualViewportHeight > 0)) return 0;
  const offset = Number.isFinite(visualViewportOffsetTop) ? visualViewportOffsetTop : 0;
  return Math.max(0, layoutViewportHeight - (visualViewportHeight + offset));
}

/** Is this a change worth writing to the DOM? */
export function insetChangeIsMeaningful(previousPx: number, nextPx: number): boolean {
  return Math.abs(nextPx - previousPx) >= KEYBOARD_INSET_DEAD_BAND_PX;
}
